import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";
import { ArrowRight, FileText, BookOpen, BarChart3, GitCompareArrows, Compass } from "lucide-react";
import CategoryBadge from "./components/CategoryBadge";

const CATEGORIES = [
  { key: "findings",      label: "Findings",        href: "/research/findings",     icon: BarChart3,        desc: "Short research observations and discoveries." },
  { key: "teardowns",     label: "Teardowns",       href: "/research/teardowns",    icon: GitCompareArrows, desc: "Comparative analysis between companies." },
  { key: "reports",       label: "Industry Reports",href: "/research/reports",      icon: BookOpen,         desc: "Long-form research publications." },
  { key: "case-studies",  label: "Case Studies",    href: "/research/case-studies", icon: FileText,         desc: "Audit outcomes and score improvements." },
  { key: "resources",     label: "Resources",       href: "/research/resources",    icon: BookOpen,         desc: "Evergreen explainers and educational content." },
  { key: "methodology",   label: "Methodology",     href: "/research/methodology",  icon: Compass,          desc: "The AI Visibility Score™ framework." },
];

export default function ResearchHub() {
  const [data, setData] = useState({ featured: [], latest: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/research/featured");
        setData(data);
      } catch (e) {
        // soft-fail; show empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="research-hub">
      <Helmet>
        <title>The AI Visibility Index™ — Research from The Entity Agency</title>
        <meta name="description" content="Research and analysis on how AI systems understand, recommend, and rank businesses. Findings, teardowns, industry reports, and case studies from The Entity Agency." />
        <meta property="og:title" content="The AI Visibility Index™" />
        <meta property="og:description" content="Research on how AI systems perceive, describe, and recommend businesses." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://theentityagency.com/research" />
      </Helmet>

      {/* HERO */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <p className="font-mono-eav text-[11px] tracking-[0.22em] uppercase text-[#b8963e]">
            The Entity Agency · Research
          </p>
          <h1 className="mt-4 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-[#f4efe6] leading-[1.05]"
              style={{ fontWeight: 500 }}>
            The AI Visibility Index™
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#c9bca4] leading-relaxed">
            Research and analysis on how AI systems understand, recommend, and rank businesses.
          </p>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="research-category-pills">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={c.href}
                    data-testid={`research-cat-pill-${c.key}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-[#2a2620] hover:border-[#b8963e] hover:bg-[#b8963e]/5 text-sm text-[#c9bca4] hover:text-[#f4efe6] transition-colors">
                <c.icon className="w-4 h-4 text-[#b8963e]" />
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="border-b border-[#2a2620]" data-testid="research-featured-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif-display text-3xl text-[#f4efe6]">Featured research</h2>
            <Link to="/research/findings" className="text-sm text-[#b8963e] hover:underline">
              All findings →
            </Link>
          </div>
          {loading ? (
            <p className="text-[#8a7e6d]">Loading…</p>
          ) : data.featured.length === 0 ? (
            <p className="text-[#8a7e6d]">No featured articles yet.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FeaturedCard item={data.featured[0]} large />
              <div className="grid gap-6">
                {data.featured.slice(1, 4).map((it) => <FeaturedCard key={it.id} item={it} />)}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* LATEST */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <h2 className="font-serif-display text-3xl text-[#f4efe6] mb-8">Latest research</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="research-latest-grid">
            {data.latest.map((it) => <ArticleCard key={it.id} item={it} />)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <h2 className="font-serif-display text-3xl text-[#f4efe6] mb-8">Browse by section</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CATEGORIES.map((c) => (
              <Link key={c.key} to={c.href}
                    data-testid={`research-section-card-${c.key}`}
                    className="block p-6 border border-[#2a2620] hover:border-[#b8963e] rounded-sm transition-colors group">
                <div className="flex items-center gap-3 mb-3">
                  <c.icon className="w-5 h-5 text-[#b8963e]" />
                  <h3 className="font-serif-display text-2xl text-[#f4efe6] group-hover:text-[#b8963e] transition-colors">
                    {c.label}
                  </h3>
                </div>
                <p className="text-sm text-[#c9bca4]">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-mono-eav text-[11px] tracking-[0.18em] uppercase text-[#b8963e]">
                  Open <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}


function FeaturedCard({ item, large = false }) {
  return (
    <Link to={`/research/${item.category}/${item.slug}`}
          data-testid={`research-featured-card-${item.slug}`}
          className="block group">
      <article className={`border border-[#2a2620] hover:border-[#b8963e] rounded-sm overflow-hidden transition-colors ${large ? "h-full" : ""}`}>
        {item.cover_image_data_url ? (
          <div className={`bg-[#1a1612] ${large ? "aspect-[16/10]" : "aspect-[16/9]"} overflow-hidden`}>
            <img src={item.cover_image_data_url} alt={item.title}
                 className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
          </div>
        ) : (
          <div className={`bg-gradient-to-br from-[#1a1612] to-[#2a2620] ${large ? "aspect-[16/10]" : "aspect-[16/9]"} flex items-center justify-center`}>
            <span className="font-mono-eav text-xs tracking-[0.3em] uppercase text-[#b8963e]/40">
              {item.category}
            </span>
          </div>
        )}
        <div className="p-6">
          <CategoryBadge category={item.category} />
          <h3 className={`mt-3 font-serif-display text-[#f4efe6] group-hover:text-[#b8963e] transition-colors leading-tight ${large ? "text-3xl" : "text-xl"}`}>
            {item.title}
          </h3>
          {item.excerpt && (
            <p className="mt-3 text-sm text-[#c9bca4] line-clamp-3">{item.excerpt}</p>
          )}
        </div>
      </article>
    </Link>
  );
}


export function ArticleCard({ item }) {
  return (
    <Link to={`/research/${item.category}/${item.slug}`}
          data-testid={`research-article-card-${item.slug}`}
          className="block group">
      <article className="border border-[#2a2620] hover:border-[#b8963e] rounded-sm overflow-hidden transition-colors h-full flex flex-col">
        {item.cover_image_data_url ? (
          <div className="bg-[#1a1612] aspect-[16/9] overflow-hidden">
            <img src={item.cover_image_data_url} alt={item.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="bg-gradient-to-br from-[#1a1612] to-[#2a2620] aspect-[16/9] flex items-center justify-center">
            <span className="font-mono-eav text-[11px] tracking-[0.3em] uppercase text-[#b8963e]/40">
              {item.category}
            </span>
          </div>
        )}
        <div className="p-5 flex-1 flex flex-col">
          <CategoryBadge category={item.category} />
          <h3 className="mt-2 font-serif-display text-lg text-[#f4efe6] group-hover:text-[#b8963e] transition-colors leading-snug">
            {item.title}
          </h3>
          {item.excerpt && (
            <p className="mt-2 text-sm text-[#c9bca4] line-clamp-2 flex-1">{item.excerpt}</p>
          )}
          {item.industry && (
            <p className="mt-3 font-mono-eav text-[10px] tracking-[0.2em] uppercase text-[#8a7e6d]">
              {item.industry}
            </p>
          )}
        </div>
      </article>
    </Link>
  );
}
